import { useEffect, useRef, useState, type ReactNode } from "react";
import { supabase } from "../lib/supabase";
import { href } from "../lib/route";
import { fetchAssignedWorkoutTree } from "../lib/assignedWorkoutTree";
import { overviewState, type WorkoutPreview } from "../lib/workoutPreview";
import { LIBRARY_ROWS_SELECT, readLibraryRows } from "../lib/libraryRows";
import { exerciseIdsOf } from "../lib/phoneOverview";
import { duplicateWorkout } from "../lib/duplicateWorkout";
import { duplicateDeps, existingTemplateNames, readWorkoutUsage, removeDeps } from "../lib/workoutActionDeps";
import { removeWorkout, removedSentence, type WorkoutUsage } from "../lib/archiveWorkout";
import { RemoveConfirm } from "./RemoveWorkoutConfirm";

/* W17d — THE WORKOUT AT A GLANCE, AS A WINDOW (his word, 2026-09-19 08:24; §7.1).
 *
 * *"i don't like just hovering over a workout and it expanding. better only way to see it would
 * be to click, or have an icon somewhere on the workout row, that ONLY when we hover over it, it
 * will show, but not expanding the row, by showing us a window, with some option/button to edit,
 * or even delete, or duplicate, which if edit is clicked, will turn this window in the real one
 * that would show if we clicked the row. so that first view of hovering over we should be able to
 * see as much as possible of the workout in 1 place, and tidy"*
 *
 * 🔴 IT REPLACES W17's HOVER PREVIEW RATHER THAN SITTING BESIDE IT. The panel that grew out of
 * the row is gone — the row no longer changes shape at all — and everything it drew is here,
 * UNCAPPED. `workoutOverview` is `workoutPreview` with the caps off, not a second traversal, so
 * a section cannot say one thing in this window and another in the builder one click away: the
 * dose is `collapsedLabel`'s, the section's name is `sectionName`'s (none when unnamed, W104), the format's word is
 * `FORMAT_LABEL`'s and its number is `formatHeadline`'s. That identity is the whole design —
 * last night's third-surface trap was exactly a window drawing its own version of a sentence.
 *
 * 🔴 THREE ACTIONS SINCE 2026-09-19 — EDIT · DUPLICATE · DELETE — AND **DELETE MEANS ARCHIVE**
 * (his word, item 5 of the four re-raised to him; Fable's call on the shape). It was held back
 * that morning for a measured reason, and the reason is what shaped the thing that replaced it:
 * `pg_constraint` says a template delete SET NULLs `root_workout_id` on every client copy and
 * every assignment (both key on `coalesce(root_workout_id, workout_id)`, so all of them are
 * silently re-keyed) and CASCADE-deletes any assignment naming the template DIRECTLY — measured
 * the same day, `Wrist series` has zero copies and **fourteen** such assignments, because task
 * rows are assigned in place and never copied.
 *
 * So Delete archives (`coach_workouts.archived_at`, migration `20260919100000`, applied) and the
 * library lists only live templates; a REAL row delete happens only when nothing at all points at
 * the workout — no copy AND no assignment — measured at click time, in `archiveWorkout.ts`, which
 * is also where the confirm's sentence comes from. The confirm names which of the two will happen
 * rather than asking "are you sure".
 *
 * ⚠️ ESCAPE AND CLOSE ARE THE TWO WAYS OUT, AND NEITHER NEEDS A QUESTION. Unlike the builder this
 * window holds NO unsaved work — it is a reading surface — so `closeAttempt`'s discard guard has
 * nothing to guard and is deliberately absent. Closing is a NAVIGATION to the backdrop's address,
 * never `history.back()`: a coach who arrived by a pasted link has no history to go back to.
 */
/**
 * Delete's own little state machine, and every state is NAMED because three of them look alike
 * from the outside: `measuring` (the counts are being read, nothing has been decided), `confirm`
 * (the coach is reading what will happen) and `working` (it is happening). Collapsing any two
 * would make a press during the read either silently ignored or silently doubled.
 */
type RemoveState =
  | { kind: "idle" }
  | { kind: "measuring" }
  | { kind: "confirm"; usage: WorkoutUsage }
  | { kind: "working"; usage: WorkoutUsage }
  | { kind: "done"; sentence: string }
  | { kind: "failed"; message: string };

export function WorkoutOverviewWindow({ workoutId, onRemoved, day }: {
  workoutId: string;
  /** Bumps the library's reload key — the list behind cannot remount on its own. */
  onRemoved?: () => void;
  /**
   * 🔴 W115 (William, 2026-09-30 ~22:05: *"a magnifier icon, and when clicked … a same sized container, rounded in its edges,
   * so we can see what's inside"*) — THIS WINDOW OPENED FROM A CLIENT'S DAY CARD, for that day's COPY (`workoutId` is the
   * assigned copy's `coach_workouts.id`, which the same door reads). Present = the window closes to the page it was opened
   * over (`onClose`: Close, Escape, or a click on the dimmed ground), says the day in its eyebrow, and draws NONE of the
   * library's three actions — Edit, Duplicate and Delete act on a TEMPLATE, and a client's copy is changed from the card's
   * own words (View · Move… · Note… · Remove…). Absent = the library window exactly as it was.
   */
  day?: {
    eyebrow: string;
    onClose: () => void;
    /** W107's click on an exercise, kept: a row opens that exercise's editor (the page's own `ExerciseSetWindow`). */
    onPickRow?: (exerciseRowId: string) => void;
    /** W72 step 2 — next week's moves for this workout when the overlay pairs it, drawn above its sections. */
    moves?: ReactNode;
  };
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const heading = useRef<HTMLHeadingElement>(null);
  const [state, setState] =
    useState<{ kind: "loading" } | { kind: "ready"; preview: WorkoutPreview }
            | { kind: "error"; message: string }>({ kind: "loading" });
  const [duplicating, setDuplicating] = useState(false);
  // Kept apart from the load's own error: a failed duplicate must not erase the reason the
  // WORKOUT failed to read, and the two have different remedies.
  const [duplicateError, setDuplicateError] = useState<string | null>(null);
  const [removeState, setRemoveState] = useState<RemoveState>({ kind: "idle" });

  useEffect(() => {
    let cancelled = false;
    setState({ kind: "loading" });
    void (async () => {
      const r = await fetchAssignedWorkoutTree(
        supabase.rpc.bind(supabase) as Parameters<typeof fetchAssignedWorkoutTree>[0], workoutId);
      // W136 B2 S3r4 W1 — the library read the week column makes (`readLibraryRows`): a run's unit is its FIELD's.
      const lib = r.tree === null ? undefined : await readLibraryRows(
        (part) => supabase.from("coach_exercises").select(LIBRARY_ROWS_SELECT).in("id", part), exerciseIdsOf(r.tree));
      // The decision is `overviewState`'s, in the tested module — this line is plumbing.
      if (!cancelled) setState(overviewState(r, lib?.fields));
    })();
    return () => { cancelled = true; };
  }, [workoutId]);

  /* `showModal()` on an already-open dialog THROWS, so it is guarded rather than assumed — the
     same guard the builder and the exercise editor carry, for the same reason.
     ⚠️ AND FOCUS IS MOVED DELIBERATELY. `<dialog>` restores focus on `close()`, but not when the
     element is unmounted in the same tick the navigation causes — measured on the real page one
     component over, where `document.activeElement` came back as `<body>`. The backdrop screen
     takes its own heading when it becomes the foreground again. */
  useEffect(() => {
    const el = dialogRef.current;
    if (el !== null && !el.open) el.showModal();
    heading.current?.focus();
  }, []);

  const close = day !== undefined ? day.onClose : () => { window.location.hash = href({ name: "workouts" }); };

  /**
   * Duplicate, from the window — the SAME `duplicateWorkout` the library row calls.
   *
   * ⚠️ THE EXISTING NAMES ARE READ AT CLICK TIME RATHER THAN HELD, and that is better than the
   * row's version rather than merely different: `duplicateName` only needs them to avoid a
   * collision, and a list captured when the page loaded can be stale by the time a coach opens a
   * window and decides. One `select` of fifteen names on a rare action is cheap.
   *
   * ⚠️ AND A FAILURE TO READ THEM IS NOT A FAILURE TO DUPLICATE. An empty list makes
   * `duplicateName` fall back to the plain `<name> copy`, which may collide with a name already
   * in the library — a cosmetic outcome — whereas refusing the duplicate over it would be the
   * annotation stopping the thing a coach actually asked for.
   */
  async function onDuplicate() {
    if (state.kind !== "ready") return;
    setDuplicating(true);
    setDuplicateError(null);
    /* W32(7): the two callbacks and the name read moved to `workoutActionDeps` when the builder's
       `⋯` became a second caller. Nothing about them changed — their comments travelled with
       them — and `duplicateWorkout` still takes its deps, so its tests still drive the rules
       without a database. */
    const names = await existingTemplateNames();
    const outcome = await duplicateWorkout(
      duplicateDeps, { id: workoutId, name: state.preview.name }, names);
    setDuplicating(false);
    if (outcome.kind === "failed") { setDuplicateError(outcome.message); return; }
    if (outcome.kind === "partial") { setDuplicateError(outcome.message); }
    // Landing in the builder is what makes this "create FROM template" rather than "make me a
    // second row": the coach duplicated in order to change something. True on `partial` too —
    // the copy exists, and the message above travels with them is the one thing that does not,
    // so a partial stays put and says so instead.
    if (outcome.kind === "ok") {
      window.location.hash = href({ name: "workout", workoutId: outcome.id });
    }
  }

  /* WHAT POINTS AT THIS TEMPLATE is read by `readWorkoutUsage` (`workoutActionDeps.ts`) since W85 — the builder's `⋯`
     deletes too, and two copies of the two counts would be two screens deciding what points at a template. */
  /** Press 1 — measure, then SAY what will happen. Nothing is written on this press. */
  async function onDeletePressed() {
    if (state.kind !== "ready") return;
    setRemoveState({ kind: "measuring" });
    const read = await readWorkoutUsage(workoutId);
    if (read.error !== null || read.usage === null) {
      setRemoveState({
        kind: "failed",
        message: `Could not check what uses this workout, so nothing was changed: `
          + `${read.error ?? "the check returned nothing."}`,
      });
      return;
    }
    setRemoveState({ kind: "confirm", usage: read.usage });
  }

  /** Press 2 — the act. `removeWorkout` re-reads the counts and decides; this is plumbing. */
  async function onRemoveConfirmed(usage: WorkoutUsage) {
    if (state.kind !== "ready") return;
    setRemoveState({ kind: "working", usage });
    const outcome = await removeWorkout(removeDeps, { id: workoutId, name: state.preview.name }, new Date());
    if (outcome.kind === "failed") { setRemoveState({ kind: "failed", message: outcome.message }); return; }
    /* ⚠️ THE WINDOW STAYS OPEN AND SAYS WHICH OF THE TWO HAPPENED. Closing straight back to the
       list would show a row that had vanished, and a vanished row reads as a delete whichever of
       the two actually ran — which is the one thing this design must not leave ambiguous. The
       list behind reloads NOW (it cannot remount on its own), so Close lands on a true list. */
    onRemoved?.();
    setRemoveState({
      kind: "done",
      sentence: removedSentence(state.preview.name, outcome) ?? "",
    });
  }

  return (
    <dialog ref={dialogRef} className={day === undefined ? "ht-window overview-window" : "ht-window overview-window is-day"}
            aria-labelledby="overview-window-title"
            onCancel={(e) => { e.preventDefault(); close(); }}
            /* W115 — from a client's day, a click on the dimmed ground (the dialog itself, never its frame) closes it too, the
               phone window's grammar. The library window keeps its two ways out. */
            onClick={day === undefined ? undefined : (e) => { if (e.target === e.currentTarget) close(); }}>
      <div className="ht-window-frame">
        <header className="ht-window-head">
          <div className="ht-window-titles">
            <p className="page-eyebrow">{day === undefined ? "Library" : day.eyebrow}</p>
            {/* An `h2`, for the same reason the builder's title is one: this sits over a page
                whose `h1` is `Workouts`, and a second `h1` would claim to be the document title.
                `fit-content` in the CSS — a focused block-level heading draws a ring the width of
                the column, which §13 scored as its loudest regression. */}
            <h2 className="ht-title overview-window-title" id="overview-window-title"
                tabIndex={-1} ref={heading}>
              {state.kind === "ready" ? state.preview.name : "Workout"}
            </h2>
          </div>
          <button type="button" className="ht-button ht-button-quiet ht-window-close"
                  onClick={close}>Close</button>
        </header>

        <div className="ht-window-body overview-body">
          {/* ⚠️ IN FLIGHT IS A SURFACE, NEVER AN EMPTY BOX — a window that appears blank and fills
              in reads as a defect the first time and as a flicker every time after. */}
          {state.kind === "loading" && (
            <p className="ht-callout loading-line" aria-live="polite">Loading the workout…</p>
          )}

          {duplicateError !== null && (
            <p className="ht-callout overview-error" role="alert">{duplicateError}</p>
          )}

          {state.kind === "error" && (
            <p className="ht-callout overview-error" role="alert">
              This workout could not be read. {state.message}
            </p>
          )}

          {day?.moves !== undefined && <div className="overview-moves">{day.moves}</div>}

          {state.kind === "ready" && state.preview.description !== null && (
            <p className="ht-caption overview-desc">{state.preview.description}</p>
          )}

          {state.kind === "ready" && state.preview.empty && (
            <p className="ht-callout overview-empty">
              Nothing in this workout yet. Edit it to add a section.
            </p>
          )}

          {state.kind === "ready" && !state.preview.empty && (
            <ul className="overview-sections">
              {state.preview.sections.map((s, i) => (
                <li key={`${s.name ?? "loose"}-${String(i)}`}
                    className={s.implicit ? "overview-section is-loose" : "overview-section"}>
                  {/* 🔴 §6.5's LOOSE RUN DRAWS NO BAR, and the flag is what says so — never the
                      absence of a name, which is the name-shaped convention §6.5 refuses. Its
                      rows sit between the named sections exactly as they do in the builder and
                      in the player. */}
                  {/* W104 — an UNNAMED section has no generated name; its bar keeps the FORMAT word (Fable, 2026-09-30:
                      the name goes, the format stays wherever a named section's shows). Only §6.5's loose run draws none. */}
                  {!s.implicit && (
                    <div className="overview-section-bar">
                      {s.name !== null && <span className="overview-section-name">{s.name}</span>}
                      <span className="ht-caption overview-section-format">
                        {s.formatLabel}{s.formatLine === null ? "" : ` · ${s.formatLine}`}
                      </span>
                    </div>
                  )}
                  {s.description !== null && (
                    <p className="ht-caption overview-section-desc">{s.description}</p>
                  )}
                  <ul className="overview-rows">
                    {s.rows.map((r, j) => (
                      <li key={`${r.name}-${String(j)}`} className="overview-row">
                        {day?.onPickRow === undefined ? (
                          <>
                            <span className="overview-row-name">{r.name}</span>
                            <span className="ht-caption overview-row-dose">{r.dose}</span>
                          </>
                        ) : (
                          /* W115 — from a client's day a row is a control: it opens that exercise's editor. */
                          <button type="button" className="overview-row-pick"
                                  onClick={() => { day.onPickRow?.(r.exerciseRowId); }}>
                            <span className="overview-row-name">{r.name}</span>
                            <span className="ht-caption overview-row-dose">{r.dose}</span>
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
          )}
        </div>

        <footer className="ht-window-foot overview-foot">
          {/* 🔴 THE CONFIRM IS A ROW IN THE FOOTER, NOT A SECOND DIALOG. A `<dialog>` inside an
              open modal `<dialog>` is a second top-layer element over the first, and the browser
              makes the one underneath inert — so the workout the coach is deciding about would
              go grey behind the question asking about it. Here the workout stays readable while
              the sentence sits under the actions it replaces.
              ⚠️ AND IT REPLACES THEM. Edit and Duplicate are not rendered while the question is
              open: a confirm beside the actions it is confirming is a row of four buttons where
              two of them navigate away mid-decision. */}
          {(removeState.kind === "confirm" || removeState.kind === "working") && (
            <RemoveConfirm name={state.kind === "ready" ? state.preview.name : ""} usage={removeState.usage}
                           working={removeState.kind === "working"}
                           onCancel={() => { setRemoveState({ kind: "idle" }); }}
                           onConfirm={() => { void onRemoveConfirmed(removeState.usage); }} />
          )}

          {/* ⚠️ `role="status"`, NOT `alert` — it is the outcome of something the coach just did,
              not an interruption, and an alert would cut across a screen reader mid-sentence. */}
          {removeState.kind === "done" && (
            <p className="ht-callout overview-done" role="status">{removeState.sentence}</p>
          )}

          {/* 🔴 EDIT TURNS THIS WINDOW INTO THE BUILDER, IN PLACE — his words. It is an `<a>` to
              `#/workouts/<id>`, and because `backdropRoute` names `workouts` for BOTH addresses,
              `App.tsx` keeps the same backdrop mounted and swaps what the window holds: no close,
              no second dim, no scroll lost on the list behind. A button calling `showModal` on a
              second dialog would be two windows and would look like one.
              ⚠️ It is this screen's ONE `.ht-button-primary`, which §13's ladder caps at one per
              surface — Duplicate stays the neutral bordered tier. */}
          {day === undefined && (removeState.kind === "idle" || removeState.kind === "measuring"
            || removeState.kind === "failed") && (
          <div className="overview-actions">
            <a className="ht-button ht-button-primary overview-edit"
               href={href({ name: "workout", workoutId })}>Edit</a>
            {/* The neutral tier, deliberately — §13 caps a surface at ONE primary, and Edit is
                the thing his sentence makes primary here. Still a BORDERED control, which is the
                other half of that contract: the design panel scored bare-text actions as
                regressions. */}
            <button type="button" className="ht-button overview-duplicate"
                    onClick={() => { void onDuplicate(); }}
                    disabled={duplicating || state.kind !== "ready"}>
              {duplicating ? "Duplicating…" : "Duplicate"}
            </button>
            {/* 🔴 DELETE — HIS THIRD ACTION, AND IT ARCHIVES. It is a real control rather than a
                disabled promise (the shape that was held back this morning), and it is the QUIET
                tier: the neutral border belongs to Duplicate, the accent to Edit, and a
                destructive action shouting from a footer is how a coach presses it by habit. The
                error ink appears on the CONFIRM's button, where it is about to mean something. */}
            <button type="button" className="ht-button ht-button-quiet overview-delete"
                    onClick={() => { void onDeletePressed(); }}
                    disabled={removeState.kind === "measuring" || state.kind !== "ready"}>
              {removeState.kind === "measuring" ? "Checking…" : "Delete"}
            </button>
          </div>
          )}

          {removeState.kind === "failed" && (
            <p className="ht-callout overview-error overview-remove-error" role="alert">
              {removeState.message}
            </p>
          )}
        </footer>
      </div>
    </dialog>
  );
}
